import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../../shared/Modal';
import { Reservation, CancellationPreview, CancelRequest, CancelResult, RefundMethod } from '../../../types';
import { ApiReservationSite, getReservationById } from '../../../services/reservationService';
import { fetchPaymentsForReservation, PaymentRecord } from '../../../services/paymentService';
import { authService } from '../../../services/auth';
import managerApi from '../../../services/auth/managerApi';
import { FeeAdjustmentFields } from '../shared/AdminFeeControls';
import {
  FeeAdjustmentState,
  EMPTY_FEE_ADJUSTMENT,
  isAdjustmentIncomplete,
  parseAdjustmentAmount,
  buildFeeAdjustmentPayload,
} from '../shared/feeAdjustmentUtils';
import './CancellationWizard.css';

interface CancellationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: Reservation | null;
  onSuccess?: () => void;
}

type Step = 'select_sites' | 'preview' | 'configure' | 'confirm' | 'result';

interface StepDef {
  key: Step;
  label: string;
}

const AdminCancellationWizard: React.FC<CancellationWizardProps> = ({
  isOpen,
  onClose,
  reservation,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('preview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // API data
  const [preview, setPreview] = useState<CancellationPreview | null>(null);
  const [result, setResult] = useState<CancelResult | null>(null);

  // Payment data
  const [payments, setPayments] = useState<PaymentRecord[]>([]);

  // Multi-site state
  const [activeSites, setActiveSites] = useState<ApiReservationSite[]>([]);
  const [selectedCancelSites, setSelectedCancelSites] = useState<string[]>([]);

  // Form state
  const [selectedWaiveFees, setSelectedWaiveFees] = useState<string[]>([]);
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('original_payment');
  const [overrideRefund, setOverrideRefund] = useState(false);
  const [customRefundAmount, setCustomRefundAmount] = useState('');
  const [reason, setReason] = useState('');
  const [feeAdjustment, setFeeAdjustment] = useState<FeeAdjustmentState>(EMPTY_FEE_ADJUSTMENT);

  // Derived state
  const isMultiSite = activeSites.length > 1;
  const isPartialCancel = isMultiSite && selectedCancelSites.length < activeSites.length;

  // Dynamic steps based on multi-site
  const steps: StepDef[] = useMemo(() => {
    if (isMultiSite) {
      return [
        { key: 'select_sites', label: 'Select Sites' },
        { key: 'preview', label: 'Preview' },
        { key: 'configure', label: 'Configure' },
        { key: 'confirm', label: 'Confirm' },
        { key: 'result', label: 'Result' },
      ];
    }
    return [
      { key: 'preview', label: 'Preview' },
      { key: 'configure', label: 'Configure' },
      { key: 'confirm', label: 'Confirm' },
      { key: 'result', label: 'Result' },
    ];
  }, [isMultiSite]);

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  // Fetch full reservation when wizard opens
  useEffect(() => {
    if (!isOpen || !reservation) return;

    const fetchFullReservation = async () => {
      setLoading(true);
      setError(null);
      try {
        const fullRes = await getReservationById(reservation.id);

        // Fetch payment records for this reservation
        const storeId = authService.getStoreId();
        if (storeId) {
          try {
            const paymentRecords = await fetchPaymentsForReservation(reservation.id, storeId);
            setPayments(paymentRecords);
          } catch {
            // Payment fetch is non-critical; continue without it
          }
        }

        const active = (fullRes.reservation_sites || []).filter(
          (rs) => rs.status === 'active'
        );
        setActiveSites(active);

        // Auto-select all sites by default
        const allAliases = active.map((rs) => rs.site?.alias || `site-${rs.site_id}`);
        setSelectedCancelSites(allAliases);

        if (active.length <= 1) {
          // Single-site: skip to preview
          setCurrentStep('preview');
          loadPreview();
        } else {
          // Multi-site: start at site selection
          setCurrentStep('select_sites');
          setLoading(false);
        }
      } catch (err: any) {
        setError('Failed to load reservation details: ' + (err.message || 'Unknown error'));
        setLoading(false);
      }
    };

    fetchFullReservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reservation]);

  const getSiteAlias = (rs: ApiReservationSite): string => {
    return rs.site?.alias || `site-${rs.site_id}`;
  };

  const loadPreview = async () => {
    if (!reservation) return;

    setLoading(true);
    setError(null);

    try {
      const isPartial =
        isMultiSite && selectedCancelSites.length > 0 && selectedCancelSites.length < activeSites.length;
      const hasAdjustment = !isAdjustmentIncomplete(feeAdjustment) && parseAdjustmentAmount(feeAdjustment.amount) !== 0;

      let data: CancellationPreview;
      if (hasAdjustment) {
        // POST variant applies the fee adjustment and returns fee_adjustment.adjusted_refund
        const body: Record<string, any> = { ...buildFeeAdjustmentPayload(feeAdjustment) };
        if (isPartial) body.cancelled_sites = selectedCancelSites;
        data = await managerApi.post(`/reservations/${reservation.id}/cancel-preview`, body);
      } else {
        let url = `/reservations/${reservation.id}/cancel-preview`;
        if (isPartial) {
          const params = selectedCancelSites.map(s => `cancelled_sites=${encodeURIComponent(s)}`).join('&');
          url += `?${params}`;
        }
        data = await managerApi.get(url);
      }
      setPreview(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load cancellation preview');
    } finally {
      setLoading(false);
    }
  };

  const calculateFinalRefund = (): number => {
    if (!preview) return 0;

    if (overrideRefund && customRefundAmount) {
      return parseFloat(customRefundAmount) || 0;
    }

    // When per-site data is available, sum per-site refunds
    let refund: number;
    if (preview.fee_adjustment) {
      // A fee adjustment was applied at preview — its adjusted_refund is authoritative
      refund = preview.fee_adjustment.adjusted_refund;
    } else if (preview.cancelled_sites && preview.cancelled_sites.length > 0) {
      refund = preview.cancelled_sites.reduce((sum, s) => sum + s.refund_amount, 0);
    } else {
      refund = preview.policy_refund_amount;
    }

    // Add back waived non-refundable fees
    for (const fee of preview.fees) {
      if (selectedWaiveFees.includes(fee.fee_type) && fee.is_waivable) {
        refund += fee.amount;
      }
    }

    return refund;
  };

  const handleNext = () => {
    if (currentStep === 'select_sites') {
      // Validate at least one site selected, then load preview
      if (selectedCancelSites.length === 0) return;
      setCurrentStep('preview');
      loadPreview();
      return;
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].key);
    }
  };

  const handleBack = () => {
    if (currentStep === 'preview' && isMultiSite) {
      // Going back to site selection clears the preview
      setPreview(null);
      setCurrentStep('select_sites');
      return;
    }

    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].key);
    }
  };

  const handleClose = () => {
    const wasSuccessful = result?.success === true;

    // Reset all state
    setCurrentStep('preview');
    setPreview(null);
    setResult(null);
    setPayments([]);
    setActiveSites([]);
    setSelectedCancelSites([]);
    setSelectedWaiveFees([]);
    setRefundMethod('original_payment');
    setOverrideRefund(false);
    setCustomRefundAmount('');
    setReason('');
    setFeeAdjustment(EMPTY_FEE_ADJUSTMENT);
    setError(null);
    onClose();

    if (wasSuccessful && onSuccess) {
      onSuccess();
    }
  };

  const handleSubmit = async () => {
    if (!reservation) return;

    setLoading(true);
    setError(null);

    try {
      const request: CancelRequest = {
        waive_fees: selectedWaiveFees,
        refund_method: refundMethod,
        reason: reason.trim(),
        ...buildFeeAdjustmentPayload(feeAdjustment),
      };

      if (overrideRefund && customRefundAmount) {
        request.final_refund_amount = parseFloat(customRefundAmount);
      }

      // Include cancelled_sites for partial cancellation
      if (isPartialCancel) {
        request.cancelled_sites = selectedCancelSites;
      }

      const data = await managerApi.post(`/reservations/${reservation.id}/cancel`, request);
      setResult(data);
      setCurrentStep('result');
    } catch (err: any) {
      setError(err.message || 'Failed to cancel reservation');
    } finally {
      setLoading(false);
    }
  };

  const toggleWaiveFee = (feeType: string) => {
    setSelectedWaiveFees(prev =>
      prev.includes(feeType)
        ? prev.filter(f => f !== feeType)
        : [...prev, feeType]
    );
  };

  const toggleSiteSelection = (siteAlias: string) => {
    setSelectedCancelSites(prev =>
      prev.includes(siteAlias)
        ? prev.filter(s => s !== siteAlias)
        : [...prev, siteAlias]
    );
  };

  const toggleAllSites = () => {
    if (selectedCancelSites.length === activeSites.length) {
      // Deselect all
      setSelectedCancelSites([]);
    } else {
      // Select all
      setSelectedCancelSites(activeSites.map(getSiteAlias));
    }
  };

  // ======== Step Renderers ========

  const renderStepIndicator = () => (
    <div className="wizard-steps">
      {steps.slice(0, -1).map((step, index) => (
        <div
          key={step.key}
          className={`wizard-step ${index <= currentStepIndex ? 'active' : ''} ${
            index < currentStepIndex ? 'completed' : ''
          }`}
        >
          <span className="step-dot">{index < currentStepIndex ? '✓' : index + 1}</span>
          <span className="step-label">{step.label}</span>
        </div>
      ))}
    </div>
  );

  const renderSelectSitesStep = () => {
    const allSelected = selectedCancelSites.length === activeSites.length;
    const cancelLabel = allSelected
      ? `Full Cancellation: all ${activeSites.length} sites`
      : `Partial Cancellation: ${selectedCancelSites.length} of ${activeSites.length} sites`;

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">Select Sites to Cancel</h3>

        <div className="review-section">
          <div className="review-row">
            <span className="review-label">Guest:</span>
            <span className="review-value">{reservation?.guestName}</span>
          </div>
          <div className="review-row">
            <span className="review-label">Dates:</span>
            <span className="review-value">
              {reservation?.checkIn} - {reservation?.checkOut}
            </span>
          </div>
          <div className="review-row">
            <span className="review-label">Total Sites:</span>
            <span className="review-value">{activeSites.length}</span>
          </div>
        </div>

        <div className="site-selection-header">
          <label className="checkbox-option select-all-toggle">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAllSites}
            />
            <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
          </label>
        </div>

        <div className="site-selection-list">
          {activeSites.map((rs) => {
            const alias = getSiteAlias(rs);
            const isSelected = selectedCancelSites.includes(alias);
            return (
              <div
                key={rs.site_id}
                className={`site-selection-card ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleSiteSelection(alias)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSiteSelection(alias)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="site-selection-info">
                  <span className="site-selection-alias">{alias}</span>
                  <span className="site-selection-cost">${rs.site_cost?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="site-selection-occupancy">
                  {rs.adults}A {rs.children > 0 ? `${rs.children}C ` : ''}{rs.pets > 0 ? `${rs.pets}P ` : ''}{rs.vehicles > 0 ? `${rs.vehicles}V` : ''}
                </div>
                {isSelected && <span className="cancel-badge">Cancelling</span>}
                {!isSelected && <span className="active-badge">Remaining</span>}
              </div>
            );
          })}
        </div>

        <div className={`cancel-summary-box ${allSelected ? '' : 'partial'}`}>
          {cancelLabel}
        </div>

        {selectedCancelSites.length === 0 && (
          <div className="warning-box">
            Select at least one site to cancel.
          </div>
        )}
      </div>
    );
  };

  const renderPreviewStep = () => {
    const nonRefundableFees = preview?.fees.filter(f => !f.is_refundable) || [];
    const remainingSiteAliases = activeSites
      .filter(rs => !selectedCancelSites.includes(getSiteAlias(rs)))
      .map(getSiteAlias);

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">
          {isPartialCancel ? 'Partial Cancellation Preview' : 'Cancellation Preview'}
        </h3>

        {loading && <div className="loading">Loading preview...</div>}
        {error && <div className="error-message">{error}</div>}

        {preview && (
          <>
            {/* Reservation info */}
            <div className="review-section">
              <div className="review-row">
                <span className="review-label">Guest:</span>
                <span className="review-value">{reservation?.guestName}</span>
              </div>
              {isMultiSite ? (
                <>
                  <div className="review-row">
                    <span className="review-label">Cancelling:</span>
                    <span className="review-value cancel-text">
                      {selectedCancelSites.join(', ')}
                    </span>
                  </div>
                  {isPartialCancel && (
                    <div className="review-row">
                      <span className="review-label">Remaining:</span>
                      <span className="review-value active-text">
                        {remainingSiteAliases.join(', ')}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="review-row">
                  <span className="review-label">Site:</span>
                  <span className="review-value">{reservation?.siteAlias}</span>
                </div>
              )}
              <div className="review-row">
                <span className="review-label">Dates:</span>
                <span className="review-value">
                  {reservation?.checkIn} - {reservation?.checkOut}
                </span>
              </div>
            </div>

            {/* Original payment info for manager reference */}
            {payments.length > 0 && (
              <div className="review-section">
                <h4>Original Payment</h4>
                {payments.filter(p => p.status === 'completed').map((payment, idx) => (
                  <div key={payment.id || idx} className="payment-reference-row">
                    <div className="review-row">
                      <span className="review-label">Payment Method:</span>
                      <span className="review-value">
                        {payment.card_brand && payment.last_four_digits
                          ? `${payment.card_brand} ****${payment.last_four_digits}`
                          : payment.payment_method || 'N/A'}
                      </span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Transaction ID:</span>
                      <span className="review-value">{payment.transaction_id || 'N/A'}</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Processed:</span>
                      <span className="review-value">
                        {payment.processed_at
                          ? new Date(payment.processed_at).toLocaleString()
                          : 'N/A'}
                      </span>
                    </div>
                    {payments.filter(p => p.status === 'completed').length > 1 && (
                      <div className="review-row">
                        <span className="review-label">Amount:</span>
                        <span className="review-value">${payment.amount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Per-site breakdown when backend provides cancelled_sites data */}
            {preview.cancelled_sites && preview.cancelled_sites.length > 0 ? (
              <div className="per-site-breakdown">
                <h4>Per-Site Refund Breakdown</h4>
                <div className="per-site-cost-table">
                  <div className="per-site-cost-row header">
                    <span>Site</span>
                    <span>Site Cost</span>
                    <span>Cancel Fee</span>
                    <span>Refund</span>
                  </div>
                  {preview.cancelled_sites.map(site => (
                    <div key={site.site_alias} className="per-site-cost-row">
                      <span className="cancel-text">{site.site_alias}</span>
                      <span>${site.site_cost.toFixed(2)}</span>
                      <span className="cancel-text">-${site.cancellation_fee.toFixed(2)}</span>
                      <span>${site.refund_amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="per-site-cost-row total">
                    <span>Total</span>
                    <span>
                      ${preview.cancelled_sites.reduce((sum, s) => sum + s.site_cost, 0).toFixed(2)}
                    </span>
                    <span className="cancel-text">
                      -${preview.cancelled_sites.reduce((sum, s) => sum + s.cancellation_fee, 0).toFixed(2)}
                    </span>
                    <span>
                      ${preview.cancelled_sites.reduce((sum, s) => sum + s.refund_amount, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Fallback: reservation-level breakdown when no per-site data */
              <div className="cost-breakdown">
                <h4>Refund Calculation</h4>
                <div className="cost-row">
                  <span>Total Paid</span>
                  <span>${preview.total_paid.toFixed(2)}</span>
                </div>
                {nonRefundableFees.length > 0 && nonRefundableFees.map((fee) => (
                  <div key={fee.fee_type} className="cost-row cost-negative">
                    <span>{fee.description} (non-refundable)</span>
                    <span>-${fee.amount.toFixed(2)}</span>
                  </div>
                ))}
                <div className="cost-row cost-subtotal">
                  <span>Refundable Amount</span>
                  <span>${preview.refundable_amount.toFixed(2)}</span>
                </div>
                {preview.cancellation_fee > 0 && (
                  <div className="cost-row cost-negative">
                    <span>Cancellation Fee ({preview.rule_description || preview.policy_name})</span>
                    <span>-${preview.cancellation_fee.toFixed(2)}</span>
                  </div>
                )}
                <div className="cost-row cost-total">
                  <span>Policy Refund</span>
                  <span>${preview.policy_refund_amount.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Global fees retained info for partial cancellation */}
            {isPartialCancel && preview.global_fees_retained && preview.global_fees_retained.length > 0 && (
              <div className="info-box">
                <strong>Global Fees Retained</strong>
                <p className="config-note">These fees apply to the remaining active sites and are not refunded:</p>
                <div className="retained-fees-list">
                  {preview.global_fees_retained.map((fee) => (
                    <div key={fee.fee_type} className="retained-fee-item">
                      <span>{fee.description}</span>
                      <span>${fee.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Policy info */}
            <div className="info-box">
              <strong>Policy:</strong> {preview.policy_name}
              {preview.days_until_checkin !== undefined && (
                <span className="policy-scope-note"> — {preview.days_until_checkin} days until check-in</span>
              )}
              {isPartialCancel && (
                <span className="policy-scope-note"> (applied only to cancelled sites)</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderConfigureStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Configure Cancellation</h3>

      {preview?.admin_capabilities.can_waive_fees && preview.fees.some(f => f.is_waivable) && (
        <div className="config-section">
          <h4>Waive Fees</h4>
          <p className="config-note">You have permission to waive fees</p>
          {preview.fees.filter(f => f.is_waivable).map((fee) => (
            <label key={fee.fee_type} className="checkbox-option">
              <input
                type="checkbox"
                checked={selectedWaiveFees.includes(fee.fee_type)}
                onChange={() => toggleWaiveFee(fee.fee_type)}
              />
              <span>Waive {fee.description} (${fee.amount.toFixed(2)})</span>
            </label>
          ))}
        </div>
      )}

      <div className="config-section">
        <h4>Refund Method</h4>
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="refundMethod"
              value="original_payment"
              checked={refundMethod === 'original_payment'}
              onChange={() => setRefundMethod('original_payment')}
            />
            <span>Original payment method</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="refundMethod"
              value="store_credit"
              checked={refundMethod === 'store_credit'}
              onChange={() => setRefundMethod('store_credit')}
            />
            <span>Store credit</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="refundMethod"
              value="check"
              checked={refundMethod === 'check'}
              onChange={() => setRefundMethod('check')}
            />
            <span>Check</span>
          </label>
        </div>
      </div>

      {(preview?.admin_capabilities.can_apply_fee_adjustment ?? false) && (
        <div className="config-section">
          <h4>Refund Adjustment</h4>
          <FeeAdjustmentFields
            canApply={preview?.admin_capabilities.can_apply_fee_adjustment ?? false}
            value={feeAdjustment}
            onChange={setFeeAdjustment}
            onBlur={loadPreview}
            disabled={loading}
            amountLabel="Refund Adjustment ($)"
            hintText="Positive = increase refund (goodwill), negative = decrease refund (deduction)."
            amountPlaceholder="e.g. 8.00 (more refund) or -20.00 (less refund)"
          />
          {preview?.fee_adjustment && (
            <div className="refund-summary">
              <span>Adjusted Refund:</span>
              <span className="refund-amount">${preview.fee_adjustment.adjusted_refund.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {preview?.admin_capabilities.can_override_amount && (
        <div className="config-section">
          <label className="checkbox-option">
            <input
              type="checkbox"
              checked={overrideRefund}
              onChange={(e) => setOverrideRefund(e.target.checked)}
            />
            <span>Override refund amount</span>
          </label>
          {overrideRefund && (
            <div className="form-group">
              <label>Custom Refund Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={customRefundAmount}
                onChange={(e) => setCustomRefundAmount(e.target.value)}
                className="form-input"
                placeholder="Enter amount"
              />
            </div>
          )}
        </div>
      )}

      <div className="refund-summary">
        <span>Final Refund Amount:</span>
        <span className="refund-amount">${calculateFinalRefund().toFixed(2)}</span>
      </div>
    </div>
  );

  const renderConfirmStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">
        {isPartialCancel ? 'Confirm Partial Cancellation' : 'Confirm Cancellation'}
      </h3>

      <div className="confirm-summary">
        <div className="confirm-row">
          <span>Reservation:</span>
          <span>{reservation?.confirmationCode}</span>
        </div>
        {isMultiSite && (
          <>
            <div className="confirm-row">
              <span>Cancelling Sites:</span>
              <span className="cancel-text">{selectedCancelSites.join(', ')}</span>
            </div>
            {isPartialCancel && (
              <div className="confirm-row">
                <span>Remaining Sites:</span>
                <span className="active-text">
                  {activeSites
                    .filter(rs => !selectedCancelSites.includes(getSiteAlias(rs)))
                    .map(getSiteAlias)
                    .join(', ')}
                </span>
              </div>
            )}
          </>
        )}
        <div className="confirm-row">
          <span>Refund Amount:</span>
          <span className="highlight">${calculateFinalRefund().toFixed(2)}</span>
        </div>
        <div className="confirm-row">
          <span>Refund Method:</span>
          <span>
            {refundMethod === 'original_payment' ? 'Original Payment' :
             refundMethod === 'store_credit' ? 'Store Credit' : 'Check'}
          </span>
        </div>
        {selectedWaiveFees.length > 0 && (
          <div className="confirm-row">
            <span>Fees Waived:</span>
            <span>{selectedWaiveFees.join(', ')}</span>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Cancellation Reason <span className="required">*</span></label>
        <textarea
          className="form-textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for cancellation (required for audit trail)..."
          rows={4}
        />
        <p className="config-note reason-privacy-note">
          Shown in the internal store notification only — this reason is not revealed to the customer in their cancellation email.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="warning-box">
        {isPartialCancel ? (
          <>
            {selectedCancelSites.length} site(s) will be cancelled.
            Reservation stays active with {activeSites.length - selectedCancelSites.length} remaining site(s).
            The guest will be notified.
          </>
        ) : (
          'This action cannot be undone. The guest will be notified of the cancellation.'
        )}
      </div>
    </div>
  );

  const renderResultStep = () => (
    <div className="wizard-content result-content">
      {result?.success ? (
        <>
          <div className="result-icon success">✓</div>
          <h3 className="result-title">
            {result.is_partial || isPartialCancel ? 'Sites Cancelled' : 'Reservation Cancelled'}
          </h3>
          <div className="result-details">
            <p className="confirmation-code">
              Confirmation: <strong>{result.confirmation_code}</strong>
            </p>

            {/* Per-site results when backend provides them */}
            {result.cancelled_sites && result.cancelled_sites.length > 0 && (
              <div className="result-sites-summary">
                <div className="result-sites-group">
                  <h4>Cancelled Sites</h4>
                  {result.cancelled_sites.map(site => (
                    <div key={site.site_alias} className="site-result cancelled">
                      <span className="site-result-alias">{site.site_alias}</span>
                      <span className="site-result-refund">Refund: ${site.refund_amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {result.remaining_sites && result.remaining_sites.length > 0 && (
                  <div className="result-sites-group">
                    <h4>Remaining Active Sites</h4>
                    {result.remaining_sites.map(site => (
                      <div key={site.site_alias} className="site-result active">
                        <span className="site-result-alias">{site.site_alias}</span>
                        <span className="site-result-status">Active</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fallback: show summary without per-site details */}
            {(!result.cancelled_sites || result.cancelled_sites.length === 0) && isPartialCancel && (
              <div className="result-sites-summary">
                <p>
                  <span className="cancel-text">Cancelled:</span>{' '}
                  {selectedCancelSites.join(', ')}
                </p>
                <p>
                  <span className="active-text">Remaining:</span>{' '}
                  {activeSites
                    .filter(rs => !selectedCancelSites.includes(getSiteAlias(rs)))
                    .map(getSiteAlias)
                    .join(', ')}
                </p>
              </div>
            )}

            <p>
              Status:{' '}
              <span className={isPartialCancel ? 'status-partial' : 'status-cancelled'}>
                {isPartialCancel ? 'Active (partial cancel)' : 'Cancelled'}
              </span>
            </p>
            <p>
              Refund: ${(result.refund_details?.final_refund_amount ?? 0).toFixed(2)} via{' '}
              {result.refund_details?.refund_method === 'original_payment'
                ? 'original payment method'
                : (result.refund_details?.refund_method ?? 'N/A')}
            </p>
            {(result.refund_details?.waived_fees?.length ?? 0) > 0 && (
              <p>Waived Fees: {result.refund_details?.waived_fees?.join(', ')}</p>
            )}
            <p className="result-note">A cancellation confirmation email will be sent to the guest.</p>
          </div>
        </>
      ) : (
        <>
          <div className="result-icon error">✗</div>
          <h3 className="result-title">Cancellation Failed</h3>
          <div className="result-details">
            <p className="error-message">{error || 'An unknown error occurred'}</p>
          </div>
        </>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'select_sites':
        return renderSelectSitesStep();
      case 'preview':
        return renderPreviewStep();
      case 'configure':
        return renderConfigureStep();
      case 'confirm':
        return renderConfirmStep();
      case 'result':
        return renderResultStep();
      default:
        return null;
    }
  };

  const footer = currentStep === 'result' ? (
    <button className="btn btn-primary" onClick={handleClose}>
      DONE
    </button>
  ) : (
    <>
      <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
        CANCEL
      </button>
      {currentStepIndex > 0 && (
        <button className="btn btn-secondary" onClick={handleBack} disabled={loading}>
          ← BACK
        </button>
      )}
      {currentStep === 'confirm' ? (
        <button
          className="btn btn-danger"
          onClick={handleSubmit}
          disabled={loading || !reason.trim()}
        >
          {loading ? 'PROCESSING...' : (isPartialCancel ? 'CONFIRM PARTIAL CANCELLATION' : 'CONFIRM CANCELLATION')}
        </button>
      ) : (
        <button
          className="btn btn-danger"
          onClick={handleNext}
          disabled={
            loading ||
            (currentStep === 'preview' && !preview) ||
            (currentStep === 'select_sites' && selectedCancelSites.length === 0)
          }
        >
          NEXT →
        </button>
      )}
    </>
  );

  if (!reservation) return null;

  const titlePrefix = isPartialCancel ? 'Partial Cancel' : 'Cancel';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${titlePrefix} Reservation ${reservation.confirmationCode} - Step ${currentStepIndex + 1}/${steps.length}`}
      size="large"
      footer={footer}
    >
      <div className="cancel-wizard">
        {currentStep !== 'result' && renderStepIndicator()}
        {renderCurrentStep()}
      </div>
    </Modal>
  );
};

export default AdminCancellationWizard;
